import { siteApi } from "@api";
import {
  IframeDialogClosed,
  OpenIframeDialog,
  UpdateIframeSrcDoc,
  UpdateIframeUrl,
} from "@components/utils/IframeDialog/IframeDialog";
import { useStatsigTracking } from "@configs/statsig/hooks";
import { WSCashbackRewardUpdatedEvent, WSCashbackRewardUpdatedEventDetail } from "@constants/ws";
import useGetRewardsInfo, { RewardInfoData } from "@hooks/useGetRewardsInfo";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { RewardStatus } from "@types";
import { calculateTotalClaimableCashback } from "@utils/cashback-prompt";
import logger from "@utils/logger";
import { getDiff } from "@utils/reward";
import { isGameIframeDialogOpen } from "@utils/ui";
import BigNumber from "bignumber.js";
import { WalletStatus, WalletTxMethod, bnOrZero } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWSCashbackRewardUpdated } from "./useAppWS";
import useServerInfo from "./useServerInfo";

const GAME_IFRAME_LOAD_DELAY_MS = 100;

const CASHBACK_TYPES = [
  WalletTxMethod.InstantCashBack,
  WalletTxMethod.DailyCashBack,
  WalletTxMethod.WeeklyCashBack,
  WalletTxMethod.MonthlyCashBack,
] as const;

/**
 * Convert WalletStatus from websocket payload to RewardInfoData format
 */
function buildRewardInfoFromWalletStatus(
  walletStatus: WalletStatus | null | undefined,
  walletType: WalletTxMethod,
  utcOffset: number,
): RewardInfoData | null {
  if (!walletStatus) return null;

  const remainingSeconds = getDiff(walletStatus, utcOffset);

  return {
    remainingSeconds: remainingSeconds,
    claimPoints: bnOrZero(walletStatus?.balance ?? 0)
      .dp(2, BigNumber.ROUND_DOWN)
      .toNumber(),
    periodInSeconds:
      moment(walletStatus?.nextPeriodResetDate).diff(walletStatus?.currentPeriodResetDate, "seconds") ?? 0,
    initialStatus: remainingSeconds > 0 ? RewardStatus.LOCKED : RewardStatus.CLAIMABLE,
    minClaimPoints: bnOrZero(walletStatus?.minimumClaim ?? 0)
      .dp(2, BigNumber.ROUND_DOWN)
      .toNumber(),
    type: walletType,
  };
}

/**
 * Convert websocket cashback payload to RewardInfoData format
 * Handles partial updates where fields may be omitted - only processes fields that exist in payload
 */
function convertWSPayloadToRewardData(
  payload: WSCashbackRewardUpdatedEventDetail,
  utcOffset: number,
): Partial<Record<WalletTxMethod, RewardInfoData | null>> {
  const result: Partial<Record<WalletTxMethod, RewardInfoData | null>> = {};

  // Convert cashback types from websocket payload
  // Only process fields that are actually present in the payload (partial updates)
  const cashbackMapping = {
    [WalletTxMethod.InstantCashBack]: payload.instant,
    [WalletTxMethod.DailyCashBack]: payload.daily,
    [WalletTxMethod.WeeklyCashBack]: payload.weekly,
    [WalletTxMethod.MonthlyCashBack]: payload.monthly,
  } as const;

  for (const type of CASHBACK_TYPES) {
    const walletStatus = cashbackMapping[type];
    // Only include in result if the field exists in payload (not undefined)
    if (walletStatus !== undefined) {
      result[type] = buildRewardInfoFromWalletStatus(walletStatus, type, utcOffset);
    }
  }

  return result;
}

/**
 * Extract cashback reward data from full rewards info
 */
function extractCashbackRewardData(
  rewardsInfo: Record<WalletTxMethod, RewardInfoData | null> | null,
): Record<WalletTxMethod, RewardInfoData | null> {
  const result = {} as Record<WalletTxMethod, RewardInfoData | null>;
  for (const type of CASHBACK_TYPES) {
    result[type] = rewardsInfo?.[type] ?? null;
  }
  return result;
}

/**
 * Clear a timeout stored in a ref
 */
function clearTimeoutRef(ref: { current: NodeJS.Timeout | null }): void {
  if (ref.current) {
    clearTimeout(ref.current);
    ref.current = null;
  }
}

/**
 * Eligibility cache interface
 */
interface EligibilityCache {
  eligible: boolean;
  nextEligibleAt: moment.Moment | null;
  cachedAt: moment.Moment;
}

/**
 * Hook to manage in-game cashback prompt visibility and state
 * Checks eligibility via backend API and shows prompt when eligible during gameplay
 */
export function useInGameCashbackPrompt() {
  const serverInfo = useServerInfo();
  const { currency } = useMemberSettings();
  const { data: initialRewardsInfo } = useGetRewardsInfo();
  const { trackCashbackMiniPromptShown } = useStatsigTracking();
  const [isVisible, setIsVisible] = useState(false);
  const wasVisibleRef = useRef(false);
  const [wsRewardData, setWsRewardData] = useState<Record<WalletTxMethod, RewardInfoData | null> | null>(null);
  const [isGameIframeOpened, setIsGameIframeOpened] = useState(false);
  const eligibilityCacheRef = useRef<EligibilityCache | null>(null);
  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const srcDocUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // SDK hook for eligibility check
  const [triggerEligibilityCheck] = siteApi.useLazyGetCashbackMiniPromptEligibilityQuery();

  // Use websocket reward data if available, otherwise fall back to initial reward data
  const effectiveRewardData = useMemo(() => {
    if (wsRewardData) {
      return wsRewardData;
    }
    return extractCashbackRewardData(initialRewardsInfo);
  }, [wsRewardData, initialRewardsInfo]);

  // Calculate total claimable cashback from effective reward data
  const totalClaimable = useMemo(() => {
    return calculateTotalClaimableCashback(effectiveRewardData);
  }, [effectiveRewardData]);

  // API call guard function - prevents unnecessary API calls
  const shouldCallEligibilityAPI = useCallback((): boolean => {
    const cache = eligibilityCacheRef.current;
    // No cache exists - need to check
    if (!cache) {
      return true;
    }

    // Have nextEligibleAt and current time is before it - DON'T call API
    if (cache.nextEligibleAt && moment().isBefore(cache.nextEligibleAt)) {
      logger._console.debug("[CashbackPrompt] Skipping API call - still before nextEligibleAt", {
        nextEligibleAt: cache.nextEligibleAt.format("YYYY-MM-DD HH:mm:ss"),
        now: moment().format("YYYY-MM-DD HH:mm:ss"),
        secondsRemaining: cache.nextEligibleAt.diff(moment(), "seconds"),
      });
      return false;
    }

    // Cooldown expired or no nextEligibleAt - can call API
    return true;
  }, []);

  // Check eligibility via API
  const checkEligibility = useCallback(async () => {
    // Pre-flight checks (before API call)
    if (!isGameIframeDialogOpen()) {
      logger._console.debug("[CashbackPrompt] Not checking eligibility: Game iframe not open");
      setIsVisible(false);
      wasVisibleRef.current = false;
      return;
    }

    // Skip API call if prompt is already visible
    // The eligibility API determines whether to SHOW the prompt, not to re-check an already-visible prompt
    if (isVisible) {
      logger._console.debug("[CashbackPrompt] Prompt already visible, skipping eligibility check");
      return;
    }

    // CRITICAL: Check if we should call API (guard against unnecessary calls)
    if (!shouldCallEligibilityAPI()) {
      // Use cached eligibility result
      const cache = eligibilityCacheRef.current;
      if (cache) {
        setIsVisible(cache.eligible);
        wasVisibleRef.current = cache.eligible;
      }
      return;
    }

    // Call API only when necessary
    try {
      logger._console.debug("[CashbackPrompt] Calling eligibility API");
      const result = await triggerEligibilityCheck();

      if (result.data) {
        const { eligible, nextEligibleAt } = result.data;

        // Update cache with new result
        eligibilityCacheRef.current = {
          eligible,
          nextEligibleAt: nextEligibleAt || null,
          cachedAt: moment(),
        };

        // Update visibility based on eligibility
        const wasVisible = wasVisibleRef.current;
        setIsVisible(eligible);
        wasVisibleRef.current = eligible;

        // Track prompt shown event
        if (eligible && !wasVisible) {
          logger._console.log("[CashbackPrompt] ✅ Prompt will be shown");
          const claimableAmount = calculateTotalClaimableCashback(effectiveRewardData);
          trackCashbackMiniPromptShown({
            totalClaimable: claimableAmount,
            currency: currency,
            isInGame: isGameIframeDialogOpen(),
          });
        } else if (!eligible) {
          logger._console.debug("[CashbackPrompt] ❌ Prompt will not be shown", {
            nextEligibleAt: nextEligibleAt?.format("YYYY-MM-DD HH:mm:ss"),
          });
        }
      }
    } catch (error) {
      logger._console.error("[CashbackPrompt] Eligibility API call failed", error);
      // On error, hide prompt and don't update cache
      setIsVisible(false);
      wasVisibleRef.current = false;
    }
  }, [
    shouldCallEligibilityAPI,
    triggerEligibilityCheck,
    effectiveRewardData,
    currency,
    trackCashbackMiniPromptShown,
    isVisible,
  ]);

  // Listen to websocket cashback updated events
  useWSCashbackRewardUpdated({
    cb: ({ detail }: WSCashbackRewardUpdatedEvent) => {
      logger._console.debug("[CashbackPrompt] Websocket cashback updated event received", {
        instant: detail.instant,
        daily: detail.daily,
        weekly: detail.weekly,
        monthly: detail.monthly,
      });

      // Convert websocket payload to reward data format (partial update)
      const utcOffset = serverInfo?.utcOffset ?? 0;
      const partialUpdate = convertWSPayloadToRewardData(detail, utcOffset);

      // Merge partial update with existing websocket data or initial server data
      setWsRewardData((prev) => {
        // If no previous WS data, merge with initial server data
        const baseData = prev ?? extractCashbackRewardData(initialRewardsInfo);

        // Merge partial update into base data
        const merged = {
          ...baseData,
          ...partialUpdate,
        };
        return merged;
      });
    },
  });

  // Check eligibility when wsRewardData changes
  useEffect(() => {
    if (wsRewardData) {
      checkEligibility();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsRewardData]);

  // Handle game iframe open/close events
  useEffect(() => {
    const handleIframeOpen = (e: Event) => {
      const customEvent = e as CustomEvent<{ options?: { isGame?: boolean } }>;
      const isGameIframe = customEvent.detail?.options?.isGame === true;

      if (isGameIframe) {
        logger._console.debug("[CashbackPrompt] Game iframe opened - clearing eligibility cache");
        // Clear eligibility cache for new session
        eligibilityCacheRef.current = null;
        // Clear websocket data to force fresh fetch from server
        // This prevents showing stale data after claiming and reopening game
        setWsRewardData(null);
        // Hide any existing prompt when new game session starts
        // Don't show prompt immediately - only show when websocket events come in
        setIsVisible(false);
        wasVisibleRef.current = false;
        // Track that game iframe dialog has been opened (but game not yet loaded)
        setIsGameIframeOpened(true);
      }
    };

    const handleIframeClose = (e: Event) => {
      const customEvent = e as CustomEvent<{ options?: { isGame?: boolean } }>;
      const isGameIframe = customEvent.detail?.options?.isGame === true;

      if (isGameIframe) {
        logger._console.debug("[CashbackPrompt] Game iframe closed - clearing eligibility cache and hiding prompt");
        // Clear eligibility cache
        eligibilityCacheRef.current = null;
        // Hide prompt when game iframe closes
        setIsVisible(false);
        wasVisibleRef.current = false;
        // Clear websocket data to prevent stale data from carrying over to next session
        // This prevents race condition where websocket events arrive after close but before next open
        setWsRewardData(null);
        // Reset game iframe opened flag
        setIsGameIframeOpened(false);
      }
    };

    // Listen for iframe dialog events
    window.addEventListener(OpenIframeDialog, handleIframeOpen);
    window.addEventListener(IframeDialogClosed, handleIframeClose);

    return () => {
      window.removeEventListener(OpenIframeDialog, handleIframeOpen);
      window.removeEventListener(IframeDialogClosed, handleIframeClose);
    };
  }, []);

  // Handle iframe URL/SrcDoc updates - this indicates game has loaded after gateway fetch
  useEffect(() => {
    const handleIframeUpdate = (timeoutRef: { current: NodeJS.Timeout | null }, updateType: string) => {
      clearTimeoutRef(timeoutRef);

      if (!isGameIframeOpened) {
        return;
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;

        if (!isGameIframeDialogOpen()) {
          logger._console.debug("[CashbackPrompt] Game iframe not open after delay, skipping eligibility check");
          setIsGameIframeOpened(false);
          return;
        }

        logger._console.debug(`[CashbackPrompt] Game iframe ${updateType} updated - game loaded, checking eligibility`);

        checkEligibility();
        setIsGameIframeOpened(false);
      }, GAME_IFRAME_LOAD_DELAY_MS);
    };

    const handleIframeUrlUpdate = () => handleIframeUpdate(urlUpdateTimeoutRef, "URL");
    const handleIframeSrcDocUpdate = () => handleIframeUpdate(srcDocUpdateTimeoutRef, "SrcDoc");

    // Listen for iframe URL/SrcDoc update events
    window.addEventListener(UpdateIframeUrl, handleIframeUrlUpdate);
    window.addEventListener(UpdateIframeSrcDoc, handleIframeSrcDocUpdate);

    return () => {
      window.removeEventListener(UpdateIframeUrl, handleIframeUrlUpdate);
      window.removeEventListener(UpdateIframeSrcDoc, handleIframeSrcDocUpdate);
      clearTimeoutRef(urlUpdateTimeoutRef);
      clearTimeoutRef(srcDocUpdateTimeoutRef);
    };
  }, [isGameIframeOpened, effectiveRewardData, checkEligibility]);

  // Handle dismiss - call API to start cooldown period
  const handleDismiss = useCallback(async () => {
    logger._console.debug("[CashbackPrompt] Prompt dismissed by user");

    // Hide prompt immediately
    setIsVisible(false);
    wasVisibleRef.current = false;

    // Call API to start cooldown period
    try {
      logger._console.debug("[CashbackPrompt] Calling API on dismiss to start cooldown");
      const result = await triggerEligibilityCheck();

      if (result.data) {
        // Update cache with new nextEligibleAt (cooldown started)
        eligibilityCacheRef.current = {
          eligible: false, // User dismissed, so not eligible
          nextEligibleAt: result.data.nextEligibleAt || null,
          cachedAt: moment(),
        };

        logger._console.debug("[CashbackPrompt] Cooldown started", {
          nextEligibleAt: result.data.nextEligibleAt?.format("YYYY-MM-DD HH:mm:ss"),
        });
      }
    } catch (error) {
      // On error, clear cache so next check will call API
      logger._console.error("[CashbackPrompt] Failed to start cooldown on dismiss", error);
      eligibilityCacheRef.current = null;
    }
  }, [triggerEligibilityCheck]);

  return {
    visible: isVisible,
    totalClaimable,
    onDismiss: handleDismiss,
  };
}
